import React, { useEffect, useState } from "react";
import MockTestModal from "../component/MockTestModal";
import { Container, Row, Col, Button, Badge, Alert, Spinner } from "react-bootstrap";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import CourseRecommendationModal from "../component/CourseRecommendationModal";
import InterestModal from "../component/InterestModal";
import { resolveAssetUrl } from "../utils/assetUrl";

const normalizeAgeGroup = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "18_24" || raw === "18-24" || raw === "18-24 years") return "18_24";
  if (raw === "25_34" || raw === "25-34" || raw === "25-34 years") return "25_34";
  if (raw === "35_plus" || raw === "35+" || raw === "35+ years") return "35_plus";
  return raw;
};

const normalizeExperienceLevel = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "dont_know" || raw === "i don't know driving" || raw === "i do not know driving") return "dont_know";
  if (raw === "underconfident" || raw === "i am underconfident") return "underconfident";
  if (raw === "know_drive" || raw === "i know how to drive") return "know_drive";
  if (raw === "drive_well" || raw === "i know driving well") return "drive_well";
  return raw;
};

const normalizeLicenseStatus = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "yes" || raw === "y" || raw === "true" || raw === "1") return "yes";
  if (raw === "no" || raw === "n" || raw === "false" || raw === "0") return "no";
  return raw === "any" ? "any" : raw;
};

const getRecommendationScore = (course, answers) => {
  const duration = Number.parseInt(course.duration, 10) || 999;
  const ageGroups = Array.isArray(course.recommendation_age_groups)
    ? course.recommendation_age_groups.map(normalizeAgeGroup).filter(Boolean)
    : [];
  const experienceLevels = Array.isArray(course.recommendation_experience_levels)
    ? course.recommendation_experience_levels.map(normalizeExperienceLevel).filter(Boolean)
    : [];
  const licenseStatus = normalizeLicenseStatus(course.recommendation_license_status || "any");
  const selectedAge = normalizeAgeGroup(answers.ageGroup);
  const selectedExperience = normalizeExperienceLevel(answers.experienceLevel);
  const selectedLicense = normalizeLicenseStatus(answers.licenseStatus);

  let score = 0;
  if (ageGroups.length > 0 && !ageGroups.includes(selectedAge)) return -1;
  if (experienceLevels.length > 0 && !experienceLevels.includes(selectedExperience)) return -1;
  if (licenseStatus && licenseStatus !== "any" && licenseStatus !== selectedLicense) return -1;

  score += ageGroups.length > 0 ? 10 : 1;
  score += experienceLevels.length > 0 ? 10 : 1;
  score += licenseStatus && licenseStatus !== "any" ? 10 : 1;
  if (selectedAge === "35_plus" && duration <= 18) score += 1;
  if (selectedAge === "18_24" && duration >= 10 && duration <= 30) score += 1;

  return score;
};

const faqData = [
    {
        question: "What do I need to do before I can start driving?",
        answer:
            "You need to obtain a learner's license and understand traffic rules before starting practical driving lessons.",
    },
    {
        question: "How often should I take lessons?",
        answer:
            "It's recommended to take 2-3 lessons per week for consistent progress.",
    },
    {
        question: "Do I need to pass the theory test before I can start lessons?",
        answer:
            "No, but passing the theory test early helps you understand road signs and rules better.",
    },
    {
        question: "Can I choose to have a woman instructor?",
        answer:
            "Yes, we offer the option to select a female instructor based on availability.",
    },
];

const Home = () => {
    const [showModal, setShowModal] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoveredCourse, setHoveredCourse] = useState(null);
    const [cities, setCities] = useState([]);
    const [schools, setSchools] = useState([]);
    const [filteredSchools, setFilteredSchools] = useState([]);
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [showRecommendationModal, setShowRecommendationModal] = useState(false);
    const [showInterestModal, setShowInterestModal] = useState(false);
    const [selectedInterestCourseId, setSelectedInterestCourseId] = useState(null);
    const [appliedPreferences, setAppliedPreferences] = useState(null);

    const { publicAuth } = useApi();
    const { currentCity } = useAuth();
    const navigate = useNavigate();

    const openRegisterForCourse = (courseId) => {
        navigate("/enroll", { state: { selectedCourseId: courseId } });
    };

    const openRegisterPopup = () => {
        window.dispatchEvent(
            new CustomEvent("openAuthModal", { detail: { mode: "register" } })
        );
    };

    const openInterestForCourse = (courseId) => {
        setSelectedInterestCourseId(courseId);
        setShowInterestModal(true);
    };

    const openRecommendationModal = () => {
        setShowRecommendationModal(true);
    };

    const applyRecommendationPreferences = (answers) => {
        setAppliedPreferences(answers);
    };

    const fetchCities = async () => {
        try {
            const res = await publicAuth("cities", "GET");
            if (res.data && res.data.status !== false) {
                setCities(res.data.data || res.data);
            }
        } catch (err) {
            console.error("Error fetching cities:", err);
        }
    };

    const fetchSchoolsByCity = async (cityId) => {
        try {
            const res = await publicAuth("driving-schools", "POST", { city_id: cityId });
            if (res.data && res.data.status) {
                return res.data.data || res.data.schools || [];
            }
            return [];
        } catch (err) {
            console.error("Error fetching schools by city:", err);
            return [];
        }
    };

    const fetchCourses = async () => {
        try {
            setLoading(true);
            let url = "courses";
            if (currentCity) url += `?city=${encodeURIComponent(currentCity)}`;

            const response = await publicAuth(url, "GET");

            if (response.data.status) {
                setCourses(response.data.data);
                setError(null);
            } else {
                throw new Error(response.data.message || "Failed to fetch courses");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCities();
        fetchCourses();
    }, [currentCity]);

    // Handle city selection change
    const handleCityChange = async (cityName) => {
        setSelectedCity(cityName);
        
        if (!cityName) {
            setFilteredSchools([]);
            setSelectedSchool(null);
            return;
        }

        // Find the city object to get city_id
        const selectedCityObj = cities.find(city => city.name === cityName);
        if (selectedCityObj && selectedCityObj.id) {
            try {
                const schoolsByCity = await fetchSchoolsByCity(selectedCityObj.id);
                setFilteredSchools(schoolsByCity);
                setSelectedSchool(schoolsByCity[0] || null);
            } catch (error) {
                console.error("Error filtering schools:", error);
                setFilteredSchools([]);
                setSelectedSchool(null);
            }
        } else {
            setFilteredSchools([]);
            setSelectedSchool(null);
        }
    };

    // Handle school selection
    const handleSchoolSelect = (school) => {
        setSelectedSchool(school);
    };

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px"
        };

        const animateOnScroll = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    element.classList.remove(
                        'fadeInUp', 'fadeInLeft', 'fadeInRight',
                        'zoomIn', 'slideInUp', 'animated'
                    );
                    element.classList.add('animated');
                    const animationType = element.dataset.animation || 'fadeInUp';
                    const delay = element.dataset.delay || '0';
                    element.style.animationDelay = `${delay}ms`;
                    element.classList.add(animationType);
                    observer.unobserve(element);
                }
            });
        };

        const observer = new IntersectionObserver(animateOnScroll, observerOptions);

        const timer = setTimeout(() => {
            document.querySelectorAll('.animated-section, [data-animation]').forEach(el => {
                observer.observe(el);
            });
        }, 500);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [courses]);

    useEffect(() => {
        const $ = window.$;
        $('.owl-carousel').owlCarousel({
            loop: false,
            margin: 0,
            nav: false,
            animateOut: 'fadeOut',
            animateIn: 'fadeIn',
            active: false,
            smartSpeed: 2000,
            autoplay: true,
            autoplayTimeout: 8000,  
            navText: ['<span className="fal fa-angle-left"></span>', '<span className="fal fa-angle-right"></span>'],
            responsive: {
                0: { items: 1 },
                600: { items: 1 },
                800: { items: 1 },
                1024: { items: 1 }
            }
        });
    }, []);

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const hasPriceForCurrentCity = (course) => {
        return currentCity && course.price && course.price.total_price;
    };

    const getDisplayPrice = (course) => {
        if (hasPriceForCurrentCity(course)) {
            return `₹${String(course.price.total_price).split(".")[0]}`;
        }
        return null;
    };

    const recommendationRows = appliedPreferences
    ? [...courses]
        .map((course) => ({
          course,
          score: getRecommendationScore(course, appliedPreferences),
          duration: Number.parseInt(course.duration, 10) || 999,
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.duration - b.duration;
        })
    : [];

    const recommendationScoreMap = recommendationRows.reduce((acc, row) => {
        acc[row.course.id] = row.score;
        return acc;
    }, {});

    const recommendedCourseIds = new Set(
        recommendationRows
        .filter((row) => row.score > 0)
        .slice(0, 3)
        .map((row) => row.course.id)
    );

    const orderedCourses = appliedPreferences
        ? recommendationRows.map((row) => row.course)
        : courses;

    const renderHeroButtons = () => (
        <div className="btn-box hero-btn-box">
            <button
                type="button"
                className="theme-btn btn-one border-0"
                onClick={openRegisterPopup}
            >
                Register Now
            </button>
            <button
                type="button"
                className="theme-btn btn-four border-0"
                onClick={() => navigate("/courses")}
            >
                Find Best Course
            </button>
        </div>
    );

    return (
        <>
            <section className="banner-style-two">
                <div className="banner-carousel owl-theme owl-carousel owl-loaded owl-drag">
                    <div className="slide-item">
                        <div className="image-layer" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner/slideimage3.png)` }}></div>
                        <div className="auto-container">
                            <div className="content-box ">
                                <h2 className="text-dark">Looking for Right Driving School</h2>
                                <p className="text-dark hero-slide-text">Choosing the right driving school can shape your entire driving experience. We guide you to trusted instructors, transparent pricing, and structured lessons that ensure you gain the skills and confidence needed to drive safely and responsibly.</p>
                                {renderHeroButtons()}
                            </div>
                        </div>
                    </div>

                    
                    <div className="slide-item">
                        <div className="image-layer" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner/slideiimageflip.png)` }}></div>
                        <div className="auto-container">
                            <div className="content-box ">
                                <h2 className="text-dark">Learn to <br />Drive with Professionals</h2>
                                <p className="text-dark hero-slide-text">Learn from certified experts who help you become a confident and skilled driver with step-by-step guidance and real road practice for safe driving.</p>
                                {renderHeroButtons()}
                            </div>
                        </div>
                    </div>
                    <div className="slide-item">
                        <div className="image-layer" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner/slideimage4.png)` }}></div>
                        <div className="auto-container">
                            <div className="content-box ">
                                <h2 className="text-dark">Your journey<br /> to confident driving starts here.</h2>
                                <p className="text-dark hero-slide-text">
                                    Gain practical skills and knowledge with our certified driving instructors.
                                </p>
                                {renderHeroButtons()}
                            </div>
                        </div>
                    </div>
                    <div className="slide-item">
                        <div className="image-layer" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/banner/slideiimage2.png)` }}></div>
                        <div className="auto-container">
                            <div className="content-box ">
                                <h2 className="text-dark">More than <br />just your Driving Instructors</h2>
                                <p className="text-dark hero-slide-text">We are more than just your driving instructors – we are your mentors on the road to confidence and safety. Our experienced trainers focus on building real-world skills, responsible driving habits, and the self-assurance you need to handle every journey with ease.</p>
                                {renderHeroButtons()}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

           <section className="ourcars-section ">
  <div className="auto-container">
    <div className="row">
      <div className="col-12 wow fadeInLeft animated">
        <div className="title-text">
          <h2>Why Choose Asysta Driving School?</h2>
        </div>
        <p className="text-muted">Learning to drive is a milestone — and it deserves to be simple, safe, and stress‑free. That’s exactly what we make possible. Our platform connects you with trusted, verified driving schools in your city, ensuring you get the right training, at the right price, with zero hassle.</p>
      </div>

      <div className="col-lg-8 wow fadeInLeft animated">
        <div className="row mt-4">
          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew12.png`} className="mr-3" width="40" alt="Verified Schools Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Verified & Trusted Driving Schools</h5>
                <p className="small text-muted">Every partner school goes through a strict verification process. We check instructor experience, vehicle condition, training quality, and customer reviews. You learn only from reliable, professional trainers.</p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew16.png`} className="mr-3" width="40" alt="Transparent Pricing Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Transparent Pricing — No Hidden Charges</h5>
                <p className="small text-muted">We have a transparent pricing policy so you can compare and choose what course fits your budget. No surprises, no last‑minute add‑ons.</p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew13.png`} className="mr-3" width="40" alt="Easy Booking Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Easy Online Booking</h5>
                <p className="small text-muted">Skip the phone calls and long searches. Book your driving lessons online in just a few clicks — anytime, anywhere.</p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew7.png`} className="mr-3" width="40" alt="Flexible Schedules Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Flexible Schedules</h5>
                <p className="small text-muted">Morning, evening, weekends — choose a time that works for you. Our partner schools offer flexible slots to match your routine.</p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew14.png`} className="mr-3" width="40" alt="Car Choice Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Learn in Your Preferred Car*</h5>
                <p className="small text-muted">Whether you want to learn in a manual or automatic, hatchback or sedan, we help you find the right match.<br/><em className="text-muted">*Subject to availability at ASYSTA Partner school</em></p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew17.png`} className="mr-3" width="40" alt="Real Reviews Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Real Reviews from Real Learners</h5>
                <p className="small text-muted">Make informed decisions with genuine ratings and feedback from learners who've trained with our partner schools. Post course completion, you can share your reviews to help future learners.</p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew15.png`} className="mr-3" width="40" alt="Support Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">End‑to‑End Support</h5>
                <p className="small text-muted">From choosing the right school to completing your lessons, our support team stays with you throughout your learning journey.</p>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="d-flex align-items-start h-100  shadow-sm p-3 rounded">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/iconnew11.png`} className="mr-3" width="40" alt="Correct Driving Icon" />
              <div>
                <h5 className="mb-1 font-weight-bold ct">Driving Correctly, Not Just Driving</h5>
                <p className="small text-muted">We focus on helping you learn proper techniques — steering control, safe braking, lane discipline, and defensive driving skills that make you a safer, smarter driver on Indian roads.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-lg-4 mt-5 mt-lg-0 wow fadeInUp animated">
        <div className="bg-light p-4 rounded shadow-sm h-100">
          <h4 className="title-text font-weight-bold ct">A Smarter Way to Learn Driving</h4>
          <p className="text-muted">Traditional driving school searches can be confusing. We bring clarity, transparency, and convenience to the process. With us, you choose and book your driving lessons in minutes, as per the best time that suits your needs.</p>
          
          <div className="mt-4 pt-3 border-top">
            <h5 className="font-weight-bold mb-3 ct">Our Mission</h5>
            <p className="text-muted">To make driving education accessible, transparent, and reliable for every learner in India — while helping local driving schools grow their business.</p>
          </div>
          
          <div className="mt-4 pt-3 border-top">
            <h5 className="font-weight-bold mb-3 ct">Your Journey Starts With Us</h5>
            <p className="text-muted mb-4">Whether you're a first‑time learner or someone looking to refresh your skills, we make the process smooth and trustworthy.</p>
            <a href="#" className="theme-btn btn-one w-100 text-center">Get Started Today</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

         <section className="about-section animated-section about-bg" data-animation="fadeInUp">
  <div className="auto-container">
    <div className="row clearfix">
      <div className="col-lg-8 col-md-12 col-sm-12 big-column">
        <div className="inner-box animated-section px-3" data-animation="fadeInLeft">
          <div className="title-text animated-section" data-animation="fadeInUp">
            <h2>ASYSTA — Advance & Systematic Training Academy</h2>
          </div>
          <div className="row clearfix">
            <div className="col-lg-6 col-md-6 col-sm-12 image-column animated-section" data-animation="zoomIn">
              <div className="image-box feature-item">
                <figure className="image">
                  <img src={`${process.env.PUBLIC_URL}/assets/images/banner/trafic-lights.jpg`} alt="Traffic Lights - ASYSTA Driving Training" />
                </figure>
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 content-column animated-section" data-animation="fadeInRight">
              <div className="content-box">
                <div className="text animated-section" data-animation="fadeInUp" data-delay="100">
                  <p>Created with a simple belief: learning to drive should be structured, modern, and truly effective. Today's new‑generation learners expect more than just basic steering lessons. They want clarity, confidence, and a training experience that prepares them for real‑world driving, not just a license test and a support system that stays with them long after their training ends.</p>
                  <p>We built ASYSTA to bridge that gap and deliver exactly that.</p>
                </div>
               <div
  className="btn-box animated-section"
  data-animation="fadeInUp"
  data-delay="200"
>
  <Link to="/about" className="theme-btn btn-one">
    Read More
  </Link>
</div>
              </div>
            </div>
          </div>
          <div className="row clearfix mt-4">
            <div className="col-12">
              <div className="content-box animated-section" data-animation="fadeInUp" data-delay="300">
                <h4 className="mb-3 animated-section ct" data-animation="fadeInUp">Our Purpose</h4>
                <div className="text animated-section" data-animation="fadeInUp" data-delay="400">
                  <p>Driving is a life skill, and mastering it requires more than knowing how to move a car. ASYSTA is committed to transforming the way India learns driving by introducing a phase‑by‑phase training approach that builds skills progressively and systematically.</p>
                  <p>Our goal is to help learners imbibe driving habits that match international standards, adapted thoughtfully for Indian roads, traffic patterns, and everyday challenges.</p>
                  <p>Our vision goes beyond driving lessons and we are creating a complete, end‑to‑end ecosystem for every new driver — from the moment they decide to learn driving to the day they confidently own and maintain their vehicle.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-lg-4 col-md-12 col-sm-12 small-column animated-section" data-animation="fadeInRight" data-delay="200">
        <div className="about-service feature-item px-3">
          <div className="shape" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/shape/shape-11.png)` }}></div>
          <figure className="image animated-section" data-animation="zoomIn">
            <img src={`${process.env.PUBLIC_URL}/assets/images/banner/student-driver.jpg`} alt="ASYSTA Student Driver Training" />
          </figure>
          <div className="text">
            <h6 className="pb-2 animated-section ct" data-animation="fadeInUp">Here's what you can come to expect as a student of Asysta Driving School:</h6>
            <ul className="list clearfix">
              <li className="animated-section" data-animation="fadeInRight" data-delay="100">Proficient Training Experts</li>
              <li className="animated-section" data-animation="fadeInRight" data-delay="200">Practical Learning through State-Of-The-Art Technology</li>
              <li className="animated-section" data-animation="fadeInRight" data-delay="300">Educative Theoretical Sessions</li>
              <li className="animated-section" data-animation="fadeInRight" data-delay="400">Systematic Phase-by-Phase Training</li>
              <li className="animated-section" data-animation="fadeInRight" data-delay="500">International Standard Driving Habits</li>
              <li className="animated-section" data-animation="fadeInRight" data-delay="600">End-to-End Driver Ecosystem Support</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section className="about-section what-we-stand-section">
  <div className="auto-container">
    <div className="row clearfix">
      <div className="col-lg-12 col-md-12 col-sm-12 big-column">
        <div className="px-3">
          <div className="title-text text-dark">
            <h2>What We Stand For</h2>
          </div>
          
          <div className="row clearfix mb-4">
            <div className="col-lg-6 col-md-6 col-sm-12 shadow-sm p-3 rounded">
              <div className="feature-block">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-graph-up-arrow me-3 text-primary fs-4" ></i>
                  <h4 className="feature-title mb-0 ct">Advance Learning</h4>
                </div>
                <div className="text">
                  <p>We bring modern training methods, structured modules, and a learner‑first approach to ensure every student understands not just what to do, but why it matters.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 shadow-sm p-3 rounded">
              <div className="feature-block">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-diagram-3 me-3 text-primary fs-4" ></i>
                  <h4 className="feature-title mb-0 ct">Systematic Training</h4>
                </div>
                <div className="text">
                  <p>Our phased curriculum breaks down driving into clear, manageable stages — from basic control to advanced road sense — helping learners build confidence step by step.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row clearfix mb-4">
            <div className="col-lg-12 shadow-sm p-3 rounded">
              <div className="feature-block">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-geo-alt me-3 text-primary fs-4" ></i>
                  <h4 className="feature-title mb-0 ct">Real‑World Readiness</h4>
                </div>
                <div className="text">
                  <p>Indian roads demand awareness, patience, and smart decision‑making. ASYSTA prepares learners for real traffic scenarios, not just textbook situations.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row clearfix mb-4">
            <div className="col-lg-12 shadow-sm p-3 rounded">
              <div className="content-box">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle me-3 text-primary fs-4" ></i>
                  <h4 className="mb-0 ct">Driving Correctly, Not Just Driving</h4>
                </div>
                <div className="text">
                  <p>We believe that long‑term driving success comes from learning the right techniques from day one. Our training focuses on safe habits, proper control, lane discipline, and responsible driving behaviour that lasts a lifetime.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row clearfix">
            <div className="col-lg-12 shadow-sm p-3 rounded">
              <div className="content-box">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-wrench-adjustable-circle me-3 text-primary fs-4" ></i>
                  <h4 className="mb-0 ct">A Complete Automotive Ecosystem</h4>
                </div>
                <div className="text">
                  <p>We support learners beyond training by offering:</p>
                  <ul className="list clearfix mt-2">
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-car-front me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Guidance on essential car accessories</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-tools me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Recommendations for nearby mechanics</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-droplet me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Car‑wash and detailing options</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-shield-check me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Access to trusted automotive services</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-gear-wide-connected me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Local driving‑related resources and support</span>
                    </li>
                  </ul>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</section>

<section
  className="about-section animated-section"
  data-animation="fadeInUp"
>
  <div className="auto-container">

    {/* TOP CENTER HEADING */}
    <div
      className="sec-title centered animated-section mb-5"
      data-animation="fadeInUp"
    >
      <h2>
        ASYSTA becomes the learner's go-to companion for everything they need
        on their driving journey
      </h2>

      <p className="mt-3">
        <strong>Your journey starts with ASYSTA</strong>
      </p>
    </div>

    <div className="row clearfix align-items-center">

      {/* LEFT SIDE – IMAGE */}
      <div
        className="col-lg-5 col-md-12 col-sm-12 image-column animated-section"
        data-animation="fadeInLeft"
      >
        <div className="image-box feature-item">
          <figure
            className="image animated-section"
            data-animation="zoomIn"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/banner/course4.png`}
              alt="ASYSTA Vision and Mission"
            />
          </figure>
        </div>
      </div>

      {/* RIGHT SIDE – CONTENT */}
      <div
        className="col-lg-7 col-md-12 col-sm-12 content-column animated-section"
        data-animation="fadeInRight"
      >
        <div
          className="title-text animated-section mb-1"
          data-animation="fadeInUp"
        >
          <h2 className="ct">Our Mission</h2>
        </div>

        <div className="px-3">
          <div
            className="content-box animated-section"
            data-animation="fadeInUp"
            data-delay="300"
          >
            <div className="text">
              <p>
                Our mission is to empower new-age learners with a complete,
                end-to-end driving ecosystem that goes far beyond traditional
                training. ASYSTA delivers modern, structured, and internationally
                aligned driving education while becoming a one-stop solution for
                everything a new driver needs.
              </p>
              <p>
                From selecting the right driving course to buying essential
                accessories, and from finding trusted mechanics to locating nearby
                car-wash and automotive services — we support learners through every
                stage of their driving journey.
              </p>
              <p>
                We are committed to making driving education smarter, safer, and
                more convenient by integrating learning, guidance, and essential
                automotive services into one seamless platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* VISION SECTION */}
      <div className="col-12 mt-4">
        <div
          className="title-text animated-section mb-1"
          data-animation="fadeInUp"
        >
          <h2 className="ct">Our Vision</h2>
        </div>

        <div
          className="content-box animated-section"
          data-animation="fadeInUp"
          data-delay="100"
        >
          <div className="text">
            <p>
              To create a new generation of confident, responsible, and skilled
              drivers who understand the science of driving, respect the road,
              and realise the importance of a clean and well-maintained vehicle.
            </p>
            <p>
              We believe that responsible driving and proper vehicle care directly
              contribute to safer streets across India.
            </p>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>

{/* ========== UPDATED COURSES SECTION - EXACTLY LIKE COURSE PAGE ========== */}
<section className="courses-section py-5">
  <div
    className="position-absolute w-100 h-100 top-0 start-0 bg-pattern"
    style={{
      backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/shape/shape-15.png)`,
      backgroundRepeat: 'repeat',
      opacity: 0.05,
      zIndex: -1
    }}
  ></div>

  <Container>
    {/* Header */}
    <div className="text-center mb-5 animated-section" data-animation="fadeInUp">
      <Badge
        className="px-3 py-2 mb-2"
        style={{
          background: "#eef3ff",
          color: "#1c3894",
          border: "1px solid #d8e2ff",
          borderRadius: "999px",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
        }}
      >
        Smart Course Selection
      </Badge>
      <h2 className="fw-bold mb-2" style={{ fontSize: "clamp(1.8rem, 3.8vw, 3.2rem)", lineHeight: 1.02 }}>
        Driving Courses That Fit Your Journey
      </h2>
      <p className="text-muted mb-0" style={{ fontSize: "clamp(0.98rem, 1.6vw, 1.25rem)" }}>
        {currentCity
          ? `Explore focused training in ${currentCity}, matched to your goals.`
          : "Explore focused training, matched to your goals."}
      </p>

      {!currentCity && (
        <Alert variant="warning" className="mt-3 d-inline-block">
          <i className="bi bi-info-circle me-2"></i>
          Set your location to see course prices
        </Alert>
      )}
    </div>

    {/* Recommendation Alert */}
    {appliedPreferences ? (
      <Alert
        variant="primary"
        className="d-flex flex-wrap align-items-center justify-content-between gap-3 rounded-4 border-0 mb-4"
        style={{
          background: "linear-gradient(135deg, #eef3ff 0%, #e8efff 100%)",
          color: "#16307f",
          boxShadow: "0 12px 28px rgba(26, 56, 148, 0.08)",
        }}
      >
        <div>
          Recommended matches are now shown first.
        </div>
        <Button
          variant="outline-primary"
          className="rounded-pill px-4"
          onClick={() => setAppliedPreferences(null)}
        >
          Reset Filter
        </Button>
      </Alert>
    ) : null}

    {/* Quick Recommendation CTA */}
    <div
      className="animated-section mb-4"
      data-animation="fadeInUp"
      data-delay="80"
      style={{
        borderRadius: "22px",
        padding: "16px 20px",
        background: "linear-gradient(135deg, #f6f8ff 0%, #edf2ff 100%)",
        border: "1px solid #dbe4ff",
        boxShadow: "0 14px 28px rgba(24, 48, 120, 0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "18px",
        }}
      >
        <div>
          <div style={{ fontSize: "1rem", fontWeight: 800, color: "#132b8f", marginBottom: "2px" }}>
            Need a quick recommendation?
          </div>
          <div style={{ fontSize: "0.9rem", color: "#4b5d87" }}>
            Answer 5 questions and we will surface your best-fit courses.
          </div>
        </div>
        <button
          type="button"
          onClick={openRecommendationModal}
          style={{
            border: "0",
            borderRadius: "10px",
            background: "#1f2799",
            color: "#ffffff",
            padding: "10px 18px",
            fontSize: "0.84rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            boxShadow: "0 16px 28px rgba(31, 39, 153, 0.25)",
          }}
        >
          Get Recommended Courses
        </button>
      </div>
    </div>

    {/* Loading State */}
    {loading && (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading courses...</p>
      </Container>
    )}

    {/* Error State */}
    {error && !loading && (
      <Container className="text-center py-5">
        <Alert variant="danger">Error: {error}</Alert>
      </Container>
    )}

    {/* No Courses State */}
    {!loading && !error && courses.length === 0 && (
      <Container className="text-center py-5">
        <Alert variant="info">
          {currentCity ? `No courses available in ${currentCity}` : "No courses available"}
        </Alert>
      </Container>
    )}

    {/* Courses List */}
    {!loading && !error && orderedCourses.length > 0 && orderedCourses.map((course, index) => {
      const recommendationMeta = {
        isRecommended: Boolean(appliedPreferences) && recommendedCourseIds.has(course.id),
        score: recommendationScoreMap[course.id] || 0,
      };

      return (
        <div
          key={course.id}
          className={`mb-4 pb-4 ${index < orderedCourses.length - 1 ? "border-bottom" : ""}`}
          style={{
            borderRadius: "28px",
            padding: "22px",
            background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
            boxShadow: "0 18px 38px rgba(21, 38, 77, 0.06)",
            border: "1px solid #edf1fb",
          }}
        >
          <Row className={`align-items-center g-5 `}>
            
             {/* CONTENT */}
            <Col
              lg={6}
              className="animated-section"
              data-animation={index % 2 === 0 ? "fadeInRight" : "fadeInLeft"}
              data-delay={index * 100 + 100}
            >
              <div >
                
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    <Badge bg="primary" className="fs-6 px-3 py-2">Learner Course</Badge>
                    {recommendationMeta.isRecommended ? (
                      <Badge
                        className="fs-6 px-3 py-2 d-inline-flex align-items-center"
                        style={{
                          background: "linear-gradient(135deg, #fff7cc 0%, #ffd24d 100%)",
                          color: "#6b4b00",
                          border: "1px solid #f1c232",
                          boxShadow: "0 10px 18px rgba(241, 194, 50, 0.22)",
                          gap: "8px",
                        }}
                      >
                        <i className="bi bi-award-fill" aria-hidden="true"></i>
                        Recommended
                      </Badge>
                    ) : null}
                  </div>

                  {hasPriceForCurrentCity(course) && (
                    <div className="price-tag bg-danger text-white p-2 px-3 rounded-pill shadow-sm">
                      <span className="d-block fs-6 fw-normal">Starting from</span>
                      <span className="d-block fs-4 fw-bold">{getDisplayPrice(course)}</span>
                    </div>
                  )}
                </div>

                <h2 className="fw-bold mb-3 text-danger">{course.title}</h2>
                <p>{course.description}</p>

                <div className="course-details-card bg-light p-2 rounded-4 mb-3">
                  <Row className="g-3">
                    <Col sm={6}>
                      <div className="d-flex align-items-center">
                        <div className="bg-white p-3 rounded-3 me-3 shadow-sm">
                          <img src={`${process.env.PUBLIC_URL}/assets/images/icons/icon-6.png`} alt="Theory" width="32" />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-bold text-danger">Theory Session</h6>
                          <p className="mb-0 text-dark">{course.duration} Day</p>
                        </div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex align-items-center">
                        <div className="bg-white p-3 rounded-3 me-3 shadow-sm">
                          <img src={`${process.env.PUBLIC_URL}/assets/images/icons/icon-7.png`} alt="Practical" width="32" />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-bold text-danger">Practical Session</h6>
                          <p className="mb-0 text-dark">{course.practical} Hours</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="alert alert-info border-0 rounded-4 mb-3">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  Driving route will be charged separately.
                  {currentCity && <span className="ms-1">Prices for {currentCity}</span>}
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    className="rounded-pill px-4"
                    onClick={() => navigate(`/course/${course.slug}`)}
                  >
                    Explore Now
                  </Button>

                  <Button
                    variant="primary"
                    className="rounded-pill px-4"
                    onClick={() => openInterestForCourse(course.id)}
                  >
                    Register Interest
                  </Button>

                 
                </div>
              </div>
            </Col>
            {/* IMAGE */}
            <Col
              lg={6}
              className="animated-section"
              data-animation={index % 2 === 0 ? "fadeInLeft" : "fadeInRight"}
              data-delay={index * 100}
            >
              <div
                className="position-relative rounded-4 overflow-hidden shadow-lg course-image-container"
                onMouseEnter={() => setHoveredCourse(course.id)}
                onMouseLeave={() => setHoveredCourse(null)}
              >
                <img
                  src={resolveAssetUrl(course.image_url || course.image, `${process.env.PUBLIC_URL}/assets/images/course${index % 2 === 0 ? "1" : "2"}.png`)}
                  alt={course.title}
                  className={`img-fluid w-100 course-image ${hoveredCourse === course.id ? "hovered" : ""}`}
                  style={{ height: "400px", objectFit: "contain", transition: "all 0.3s ease" }}
                  onError={(e) => {
                    e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/images/course${
                      index % 2 === 0 ? "1" : "2"
                    }.png`;
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      );
    })}
  </Container>
</section>
{/* ========== END UPDATED COURSES SECTION ========== */}

            <section className="video-section centred">
                <div className="bg-layer" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/background/video-bg-2.jpg)` }}></div>
                <div className="auto-container">
                    <div className="video-btn">
                        <a href="https://www.youtube.com/watch?v=nfP5N9Yc72A&amp;t=28s" className="lightbox-image" data-caption=""><i className="flaticon-play-button-1"></i><span>Training Video</span></a>
                    </div>
                </div>
            </section>

            <section className="location-section">
                <div className="auto-container">
                    <div className="tabs-box">
                        <div className="row clearfix">
                            {/* Left Content */}
                            <div className="col-lg-5 col-md-12 col-sm-12 content-column">
                                <div className="content-box">
                                    <div className="sec-title-two">
                                        <h5>Our Locations</h5>
                                        <h2>Nearest locations to learn your driving course</h2>
                                    </div>
                                    <p>
                                        Select a city from dropdown and see driving schools with Google Maps.
                                    </p>

                                    {/* City Dropdown */}
                                    <select
                                        className="form-control"
                                        value={selectedCity}
                                        onChange={(e) => handleCityChange(e.target.value)}
                                    >
                                        <option value="">Select City</option>
                                        {cities.map((city, index) => (
                                            <option key={index} value={city.name}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Right Map Section */}
                            <div className="col-lg-7 col-md-12 col-sm-12 location-column">
                                <div className="location-box">
                                    <div className="map" style={{ height: "400px" }}>
                                        {selectedSchool ? (
                                            <iframe
                                                title="school-map"
                                                width="100%"
                                                height="100%"
                                                style={{ border: 0 }}
                                                loading="lazy"
                                                allowFullScreen
                                                src={`https://www.google.com/maps?q=${encodeURIComponent(
                                                    `${selectedSchool.address}, ${selectedSchool.city_name}, ${selectedSchool.zipcode}`
                                                )}&output=embed`}
                                            ></iframe>
                                        ) : (
                                            <iframe
                                                title="default-map"
                                                width="100%"
                                                height="100%"
                                                style={{ border: 0 }}
                                                loading="lazy"
                                                allowFullScreen
                                                src="https://www.google.com/maps?q=India&output=embed"
                                            ></iframe>
                                        )}
                                    </div>

                                    {/* Schools List */}
                                    <ul className="tab-btns tab-buttons clearfix">
                                        {filteredSchools.map((school) => (
                                            <li
                                                key={school.key || school.id}
                                                className={`tab-btn ${selectedSchool && selectedSchool.key === school.key
                                                    ? "active-btn"
                                                    : ""
                                                    }`}
                                                onClick={() => handleSchoolSelect(school)}
                                            >
                                                <div className="text">
                                                    <h5>{school.username}</h5>
                                                    <span>{school.address}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* School Details */}
                        <div className="tabs-content">
                            <div className="tab active-tab">
                                <div className="row clearfix">
                                    {filteredSchools.map((school) => (
                                        <div
                                            key={school.key || school.id}
                                            className="col-lg-4 col-md-6 col-sm-12 location-block"
                                        >
                                            <div className="single-location">
                                                <div className="text">
                                                    <div className="icon">
                                                        <i className="flaticon-pin"></i>
                                                    </div>
                                                    <h5>{school.username}</h5>
                                                    <p>
                                                        {school.address} {school.zipcode}
                                                    </p>
                                                </div>
                                                <h5>
                                                    City: <span>{school.city_name}</span>
                                                </h5>
                                                
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

           <section className="news-style-two">
  <div className="auto-container">

    <div className="sec-title-two centred">
      <h2>Essential Driving Tips</h2>
      <p>Follow these important tips to ensure safe and responsible driving.</p>
    </div>

    <div className="row clearfix">

      {[
        { title: "Wear Your Seatbelt", desc: "Always buckle up.", img: "essential1.jpg" },
        { title: "Follow Speed Limits", desc: "Drive at safe speeds.", img: "essential2.jpg" },
        { title: "Maintain Safe Distance", desc: "Keep a 3-second gap.", img: "essential3.webp" },
        { title: "Use Indicators", desc: "Signal before turning.", img: "essential4.webp" },
        { title: "Check Your Mirrors", desc: "Check mirrors often.", img: "essential5.avif" },
        { title: "Drive Carefully in Weather", desc: "Slow down in rain or fog.", img: "essential6.webp" },
        { title: "Know Traffic Signs", desc: "Follow road signs.", img: "essential7.png" },
        { title: "Brake & Accelerate Smoothly", desc: "Avoid sudden moves.", img: "essential8.jpg" },
        { title: "Check Your Vehicle", desc: "Inspect tyres & brakes.", img: "essential9.avif" },
        
      ].map((item, index) => (
        <div className="col-lg-4 col-md-6 col-sm-12 mb-4" key={index}>
          
          <div
            className="inner-box"
            style={{
              border: "1px solid #e5e5e5",
              borderRadius: "6px",
              overflow: "hidden",
              background: "#fff",
              height: "100%",
            }}
          >

            {/* Top Blue Title Bar */}
            <div
              style={{
                background: "#2f5fb3",
                color: "#fff",
                padding: "12px",
                fontWeight: "600",
                textAlign: "center",
                fontSize: "16px",
              }}
            >
              {item.title}
            </div>

            {/* Image */}
            {/* Image */}
{/* Image */}
<div
  style={{
    height: "140px",
    width: "100%",
    overflow: "hidden",
  }}
>
  <img
    src={`${process.env.PUBLIC_URL}/assets/images/news/${item.img}`}
    alt=""
    style={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    }}
  />
</div>

            {/* Bottom Blue Strip */}
            <div
              style={{
                background: "#1c3c78",
                color: "#fff",
                padding: "10px",
                textAlign: "center",
                fontSize: "14px",
              }}
            >
              {item.desc}
            </div>

          </div>

        </div>
      ))}

    </div>
  </div>
</section>
            

          <section className="service-details">
  <div
    className="shape"
    style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/shape/shape-74.png)` }}
  ></div>
  <div className="auto-container">
    <div className="service-details-content">
      <div className="content-two">
        <div className="text">
          <h2>LLR Mock Test – Prepare Smarter with ASYSTA</h2>
          <p>Before you begin practicing on the road, the Central Motor Vehicle Rules require every new driver to hold a valid Learner's Licence. To obtain it, you must clear the Learner's Licence Registration (LLR) test — the first official step toward becoming a licensed driver in India.</p>
          <p>ASYSTA's LLR Mock Test helps you understand where you stand and how prepared you are for the real exam. Whether you're from a metro city or a small town, anyone across India can take this test and assess their knowledge instantly.</p>
          <p>Our mock test follows the guidelines of the Central Motor Vehicle Rules and includes 20 multiple‑choice questions covering essential topics such as road signs and symbols, hand signals, rules of the road, and basic traffic laws and CMVR‑based guidelines.</p>
          <p>To pass the mock test, you'll need to score at least 70%, just like the actual LLR exam.</p>
          <p>Getting started is simple. Enter your basic details, verify with an OTP, and you're ready to begin. This quick practice session helps you build confidence and understand what to expect on the real test day.</p>
        </div>
        
        <div className="text mt-5">
          <h2>We offer two levels of mock tests to suit every visitor:</h2>
        </div>
        
        <div className="row clearfix mt-4">
          <div className="col-lg-6 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Free Mock Test (Open for All)</h5>
              <p className="text-muted mt-2">Perfect for first‑time visitors who want a quick check of their driving‑theory knowledge. No login, no commitment — just a simple way to understand where you stand.</p>
            </div>
          </div>
          
          <div className="col-lg-6 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Advanced Mock Test (For Registered Users)</h5>
              <p className="text-muted mt-2">Create a free ASYSTA account to unlock deeper, more comprehensive tests with full‑length mock exams, scenario‑based questions, performance insights, and progress tracking.</p>
            </div>
          </div>
        </div>

        <div className="text mt-5">
          <h2>How It Works</h2>
        </div>
        
        <div className="row clearfix mt-4">
          <div className="col-lg-4 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Choose your test</h5>
              <p className="text-muted mt-2">Select between Free or Advanced mock test based on your needs.</p>
            </div>
          </div>
          
          <div className="col-lg-4 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Answer questions</h5>
              <p className="text-muted mt-2">Answer all questions based on Indian road rules and RTO patterns.</p>
            </div>
          </div>
          
          <div className="col-lg-4 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Get your results</h5>
              <p className="text-muted mt-2">Submit your test to view your score instantly with detailed insights.</p>
            </div>
          </div>
          
          <div className="col-lg-4 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Review performance</h5>
              <p className="text-muted mt-2">Free users get basic score summary, registered users get detailed analysis.</p>
            </div>
          </div>
          
          <div className="col-lg-4 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Practice anytime</h5>
              <p className="text-muted mt-2">Retake tests to strengthen your understanding and track progress.</p>
            </div>
          </div>
          
          <div className="col-lg-4 col-md-6 col-sm-12 single-column shadow-sm p-3 rounded">
            <div className="single-item">
              <div className="icon-box"><i className="flaticon-checkbox-mark"></i></div>
              <h5 className="ct">Guidelines for Best Results</h5>
              <p className="text-muted mt-2">Read questions carefully, stable internet connection, track progress for registered users.</p>
            </div>
          </div>
        </div>

        <div className="inner-box mt-5">
          <div className="download-option">
            <div className="icon-box">
              <img src={`${process.env.PUBLIC_URL}/assets/images/icons/icon-52.png`} alt="Test Icon" />
            </div>
            <h4>Ready to test your knowledge?</h4>
            <span>These tests help you prepare for the Learner's License exam and improve real‑world driving awareness.</span>
          </div>
          <div className="btn-box mt-4">
            <button 
              className="theme-btn btn-four mr-3"
              onClick={() => setShowModal(true)}
            >
              FREE MOCK TEST
            </button>
            
          </div>
        </div>
      </div>
    </div>
  </div>
  <MockTestModal show={showModal} onClose={() => setShowModal(false)} />
</section>

            <section className="page-title">
                <div className="bg-layer mt-1" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/background/page-title.jpg)` }}></div>
                <div className="auto-container">
                    <div className="content-box">
                        <h1>Question & Answers</h1>
                    </div>
                </div>
            </section>
            <section className="faq-page-section">
                <div className="auto-container">
                    <div className="upper-box">
                        <span className="big-text">q&amp;a</span>
                        <div className="sec-title">
                            <h2>
                                Find answers in our <br />
                                list of frequently asked questions
                            </h2>
                        </div>
                        <div className="form-inner">
                            <form>
                                <div className="form-group">
                                    <input
                                        type="search"
                                        name="search-field"
                                        placeholder="Search question..."
                                        required
                                    />
                                    <button type="submit">
                                        <i className="flaticon-search"></i>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="accordion-inner">
                        <ul className="accordion-box">
                            {faqData.map((item, index) => (
                                <li
                                    key={index}
                                    className={`accordion block ${activeIndex === index ? "active-block" : ""
                                        }`}
                                >
                                    <div
                                        className={`acc-btn ${activeIndex === index ? "active" : ""}`}
                                        onClick={() => toggleAccordion(index)}
                                    >
                                        <div className="icon-outer"></div>
                                        <h4>
                                            <span>q:</span> {item.question}
                                        </h4>
                                    </div>
                                    {activeIndex === index && (
                                        <div className="acc-content current">
                                            <div className="text">
                                                <p>
                                                    <span>a:</span> {item.answer}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Modals */}
            <CourseRecommendationModal
                show={showRecommendationModal}
                onClose={() => setShowRecommendationModal(false)}
                courses={courses}
                onSelectCourse={openRegisterForCourse}
                onViewCourse={(course) => navigate(`/course/${course.slug}`)}
                onApplyPreferences={applyRecommendationPreferences}
            />
            <InterestModal
                show={showInterestModal}
                onClose={() => {
                setShowInterestModal(false);
                setSelectedInterestCourseId(null);
                }}
                courseId={selectedInterestCourseId}
            />
        </>
    );
};

export default Home;
